/**
 * Iter 67bw — Admin Fleet Health summary chip.
 *
 * Platform-wide YTD financial-health snapshot for ops admins:
 *   - Totals (revenue / expenses / net) across every vendor's fleet
 *   - Bucket counts (net-positive / break-even / net-negative / dormant)
 *   - Top-5 best & worst performers (with vendor attribution)
 *   - Vendors currently breaching their per-car alert thresholds — proactive
 *     ops outreach signal.
 *
 * Reads `/api/admin/fleet-health-summary` (admin-or-agent + view_payouts perm).
 * Hides itself silently on permission failure so non-fleet admins don't see
 * an error tile.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import Money from "@/components/Money";
import { Badge } from "@/components/ui/badge";
import AdminAlertDrillDownDialog from "@/components/admin/AdminAlertDrillDownDialog";
import {
  Activity, TrendingUp, TrendingDown, AlertTriangle, ArrowRight,
  CircleCheck, CircleMinus, CircleX, Moon,
} from "lucide-react";

function _Bucket({ icon: Icon, count, label, tone, testId }) {
  const toneMap = {
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    rose:    "bg-rose-50 text-rose-700 ring-rose-200",
    amber:   "bg-amber-50 text-amber-800 ring-amber-200",
    slate:   "bg-slate-100 text-slate-700 ring-slate-200",
  };
  return (
    <div className={`rounded-xl ring-1 ${toneMap[tone]} px-3 py-2.5 flex items-center gap-2.5`} data-testid={testId}>
      <Icon className="h-4 w-4 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-lg font-bold tabular-nums leading-none">{count}</div>
        <div className="text-[10px] uppercase tracking-wider font-semibold mt-0.5">{label}</div>
      </div>
    </div>
  );
}

function _PerformerRow({ row, rank, tone, testId }) {
  const ring = tone === "best"
    ? "ring-emerald-100 hover:ring-emerald-200 hover:bg-emerald-50/40"
    : "ring-rose-100 hover:ring-rose-200 hover:bg-rose-50/40";
  const rankBg = tone === "best" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700";
  const netTone = tone === "best" ? "text-emerald-700" : "text-rose-700";
  return (
    <div className={`flex items-center gap-3 p-2.5 rounded-xl bg-white ring-1 ${ring} transition-all`} data-testid={testId}>
      <div className={`flex-shrink-0 h-6 w-6 rounded-md flex items-center justify-center font-bold text-[11px] ${rankBg}`}>
        #{rank}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-neutral-900 text-xs truncate">{row.label}</div>
        <div className="text-[10px] text-neutral-500 truncate">
          {row.vendor_name}{row.plate ? ` · ${row.plate}` : ""}
        </div>
      </div>
      <div className="text-right">
        <div className={`font-bold tabular-nums text-xs ${netTone}`}><Money amount={row.net_eur} /></div>
        <div className="text-[9px] uppercase tracking-wider text-neutral-500">Net</div>
      </div>
    </div>
  );
}

export default function AdminFleetHealthChip() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [drillTarget, setDrillTarget] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/admin/fleet-health-summary")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, []);

  if (error) return null;
  if (!data) {
    return (
      <div className="rounded-2xl border border-border bg-white p-5 animate-pulse text-sm text-neutral-400" data-testid="admin-fleet-health-loading">
        Loading platform fleet health…
      </div>
    );
  }

  const totals = data.totals || {};
  const buckets = data.buckets || {};
  const alerts = data.alerts || [];
  const netTone = (totals.net || 0) >= 0 ? "text-emerald-700" : "text-rose-700";
  const netLabelTone = (totals.net || 0) >= 0 ? "text-emerald-900" : "text-rose-900";

  return (
    <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-white via-teal-50/30 to-rose-50/20 p-5 shadow-sm" data-testid="admin-fleet-health-chip">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white shadow-md shadow-teal-200">
            <Activity className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-teal-700">Platform fleet health · {data.year}</div>
            <div className="font-semibold text-neutral-900 text-sm">YTD across {totals.fleet_size || 0} cars</div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-900">Revenue</div>
            <div className="font-semibold text-emerald-800 tabular-nums" data-testid="admin-fleet-totals-revenue">
              <Money amount={totals.revenue || 0} />
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider font-bold text-rose-900">Expenses</div>
            <div className="font-semibold text-rose-800 tabular-nums" data-testid="admin-fleet-totals-expenses">
              <Money amount={totals.expenses || 0} />
            </div>
          </div>
          <div className="text-right">
            <div className={`text-[10px] uppercase tracking-wider font-bold ${netLabelTone}`}>Net</div>
            <div className={`font-bold tabular-nums ${netTone}`} data-testid="admin-fleet-totals-net">
              <Money amount={totals.net || 0} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-5">
        <_Bucket icon={CircleCheck} count={buckets.positive || 0}  label="Net positive"  tone="emerald" testId="admin-fleet-bucket-positive" />
        <_Bucket icon={CircleMinus} count={buckets.breakeven || 0} label="Break-even"   tone="amber"   testId="admin-fleet-bucket-breakeven" />
        <_Bucket icon={CircleX}     count={buckets.negative || 0}  label="Net negative" tone="rose"    testId="admin-fleet-bucket-negative" />
        <_Bucket icon={Moon}        count={buckets.dormant || 0}   label="Dormant"      tone="slate"   testId="admin-fleet-bucket-dormant" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div>
          <div className="flex items-center gap-1.5 mb-2 text-[11px] font-bold text-emerald-700 uppercase tracking-wider">
            <TrendingUp className="h-3.5 w-3.5" /> Top 5 platform-wide
          </div>
          <div className="space-y-1.5">
            {(data.best || []).map((row, i) => (
              <_PerformerRow key={row.id} row={row} rank={i + 1} tone="best" testId={`admin-fleet-best-${row.id}`} />
            ))}
            {(data.best || []).length === 0 && <div className="text-xs text-neutral-500 italic px-2 py-3">No revenue this year yet.</div>}
          </div>
        </div>
        <div>
          <div className="flex items-center gap-1.5 mb-2 text-[11px] font-bold text-rose-700 uppercase tracking-wider">
            <TrendingDown className="h-3.5 w-3.5" /> Worst 5 platform-wide
          </div>
          <div className="space-y-1.5">
            {(data.worst || []).map((row, i) => (
              <_PerformerRow key={row.id} row={row} rank={i + 1} tone="worst" testId={`admin-fleet-worst-${row.id}`} />
            ))}
            {(data.worst || []).length === 0 && (
              <div className="flex items-start gap-2 p-2.5 rounded-xl bg-amber-50/60 border border-amber-100 text-xs text-amber-900">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <span>No net-negative cars — fleet is healthy.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="rounded-xl bg-amber-50/70 border border-amber-200 p-3" data-testid="admin-fleet-alerts">
          <div className="flex items-center gap-2 mb-2 text-[11px] font-bold text-amber-900 uppercase tracking-wider">
            <AlertTriangle className="h-3.5 w-3.5" /> {alerts.length} threshold breach{alerts.length === 1 ? "" : "es"} flagged by vendors
          </div>
          <div className="flex flex-wrap gap-2">
            {alerts.slice(0, 6).map((a) => (
              <button
                type="button"
                onClick={() => setDrillTarget(a)}
                key={a.car_id}
                className="inline-flex items-center gap-1.5 bg-white rounded-full ring-1 ring-amber-200 hover:ring-amber-400 hover:bg-amber-100/40 px-2.5 py-1 text-[11px] font-medium text-amber-900 transition cursor-pointer"
                data-testid={`admin-fleet-alert-${a.car_id}`}
              >
                <span className="font-semibold">{a.vendor_name}</span>
                <span className="text-amber-700">·</span>
                <span>{a.car_label}</span>
                <Badge className="bg-amber-600 text-[9px] ml-0.5">{a.reasons[0]}</Badge>
                <ArrowRight className="h-3 w-3" />
              </button>
            ))}
            {alerts.length > 6 && (
              <span className="text-[11px] text-amber-800 italic self-center">+{alerts.length - 6} more</span>
            )}
          </div>
        </div>
      )}

      <AdminAlertDrillDownDialog
        alert={drillTarget}
        open={!!drillTarget}
        onOpenChange={(o) => !o && setDrillTarget(null)}
      />
    </div>
  );
}
